import { cn } from "../../../../lib/utils";
import { Badge } from "../../../ui/common/badge";
import { Button } from "../../../ui/common/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../ui/common/table";
import {
  VERSION_HISTORY_COLUMNS,
  VERSION_HISTORY_COPY
} from "../constants/versionHistory.constants";
import { VersionHistoryRecord } from "../types/versionHistory.types";
import { formatHistoryDate } from "../utils/versionHistory.date.utils";
import { formatCalculatedVersionLabel } from "../utils/versionHistory.utils";
import { VersionHistoryStateMessage } from "./VersionHistoryStateMessage";

interface VersionCalculationRecordsRowsProps {
  records: VersionHistoryRecord[];
  selectedRecordId: string | undefined;
  onSelectRecord: (recordId: string) => void;
}

const CALCULATION_RECORDS_HEAD_CLASS =
  "tw-text-[10.5px] tw-uppercase tw-tracking-[0.08em] tw-font-mono tw-text-muted-foreground tw-whitespace-nowrap";

/**
 * Renders one version's calculation records as a single row of the SHARED outer table (see
 * VersionHistoryTable), whose cell holds its own nested <Table>. A nested table -- rather than bare
 * rows -- is needed so the column header repeats directly above each expanded version's records:
 * the outer table's single header at the very top of the page scrolls out of view once a version
 * further down the list is expanded.
 */
export const VersionCalculationRecordsRows = ({
  records,
  selectedRecordId,
  onSelectRecord
}: VersionCalculationRecordsRowsProps) => {
  return (
    <TableRow>
      <TableCell colSpan={VERSION_HISTORY_COLUMNS.length} className="tw-p-0">
        <Table>
          <TableHeader>
            <TableRow>
              {VERSION_HISTORY_COLUMNS.map((column, index) => (
                <TableHead
                  key={column || `column-${index}`}
                  className={cn(
                    CALCULATION_RECORDS_HEAD_CLASS,
                    index === 0 && "tw-pl-8",
                    (index === 1 || index === 3) && "tw-text-left"
                  )}
                >
                  {column.split(/(\([^)]*\))/).map((part, partIndex) =>
                    part.startsWith("(") ? (
                      <span key={partIndex} className="tw-normal-case">
                        {part}
                      </span>
                    ) : (
                      part
                    )
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={VERSION_HISTORY_COLUMNS.length} className="tw-py-3">
                  <VersionHistoryStateMessage
                    tone="info"
                    message={VERSION_HISTORY_COPY.noCalculationsForVersion}
                  />
                </TableCell>
              </TableRow>
            ) : (
              records.map(record => (
                <TableRow
                  key={record.id}
                  className={cn(selectedRecordId === record.id && "tw-bg-muted/40")}
                >
                  <TableCell className="tw-pl-8 tw-text-sm tw-font-mono tw-whitespace-nowrap tw-pt-1">
                    {formatHistoryDate(record.calculatedAt)}
                  </TableCell>
                  <TableCell className="tw-text-sm tw-font-mono tw-text-left tw-pt-1">
                    {record.climateChange?.measure !== undefined
                      ? record.climateChange.measure.toFixed(2)
                      : "-"}
                  </TableCell>
                  <TableCell className="tw-text-sm tw-pt-1">
                    {record.calculateVersion ? (
                      <Badge
                        variant="outline"
                        className="tw-rounded-full tw-font-mono tw-text-xs tw-font-medium"
                      >
                        {formatCalculatedVersionLabel(record.calculateVersion)}
                      </Badge>
                    ) : (
                      formatCalculatedVersionLabel(record.calculateVersion)
                    )}
                  </TableCell>
                  <TableCell className="tw-text-sm tw-text-left tw-pt-1">
                    {record.lastEditedBy?.name ?? "Not available"}
                  </TableCell>
                  <TableCell className="tw-text-right tw-pt-1">
                    <Button
                      type="button"
                      size="sm"
                      variant={selectedRecordId === record.id ? "default" : "outline"}
                      className="tw-h-8 tw-text-xs"
                      onClick={() => onSelectRecord(record.id)}
                    >
                      {VERSION_HISTORY_COPY.details}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableCell>
    </TableRow>
  );
};
